/**
 * Daily Briefing - Din personlige AI-assistent i CRM
 *
 * Funksjonalitet:
 * - Morgenbriefing (hva har jeg i dag?)
 * - Dagoppsummering (hva gjorde jeg i dag?)
 * - Ukentlig/månedlig rapport
 *
 * Erstatter Littlebird!
 */

import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, AlertCircle, TrendingUp, Mail } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.swagner.workers.dev';

export default function DailyBriefing() {
  const [briefing, setBriefing] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('briefing'); // 'briefing' | 'summary'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  /**
   * Last inn daglig briefing
   */
  useEffect(() => {
    if (activeTab === 'briefing') {
      loadBriefing();
    }
  }, [activeTab, selectedDate]);

  const loadBriefing = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/briefing/daily?date=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Feil ved lasting av briefing');
      }

      const data = await response.json();
      setBriefing(data);

    } catch (error) {
      console.error('Briefing error:', error);
      alert('Kunne ikke laste briefing');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Last inn dagoppsummering
   */
  const loadSummary = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/briefing/summary?date=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Feil ved lasting av oppsummering');
      }

      const data = await response.json();
      setSummary(data);

    } catch (error) {
      console.error('Summary error:', error);
      alert('Kunne ikke laste oppsummering');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Render Markdown-lignende tekst
   */
  const renderMarkdown = (text) => {
    if (!text) return null;

    return text.split('\n').map((line, idx) => {
      // Headers
      if (line.startsWith('# ')) {
        return <h1 key={idx} style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '20px' }}>{line.substring(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={idx} style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '16px', marginBottom: '8px' }}>{line.substring(3)}</h2>;
      }

      // Bold text
      if (line.includes('**')) {
        const parts = line.split('**');
        return (
          <p key={idx} style={{ margin: '4px 0' }}>
            {parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}
          </p>
        );
      }

      // List items
      if (line.startsWith('- ')) {
        return <li key={idx} style={{ marginLeft: '20px' }}>{line.substring(2)}</li>;
      }

      // Horizontal rule
      if (line === '---') {
        return <hr key={idx} style={{ margin: '20px 0', border: '1px solid #e0e0e0' }} />;
      }

      // Empty line
      if (line.trim() === '') {
        return <br key={idx} />;
      }

      // Normal text
      return <p key={idx} style={{ margin: '4px 0' }}>{line}</p>;
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <Calendar size={32} />
        Din personlige assistent
      </h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
        <button
          onClick={() => setActiveTab('briefing')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: activeTab === 'briefing' ? '#0066cc' : 'transparent',
            color: activeTab === 'briefing' ? 'white' : '#333',
            cursor: 'pointer',
            borderRadius: '4px 4px 0 0',
            fontWeight: activeTab === 'briefing' ? 'bold' : 'normal'
          }}
        >
          🌅 Daglig Briefing
        </button>
        <button
          onClick={() => {
            setActiveTab('summary');
            loadSummary();
          }}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: activeTab === 'summary' ? '#0066cc' : 'transparent',
            color: activeTab === 'summary' ? 'white' : '#333',
            cursor: 'pointer',
            borderRadius: '4px 4px 0 0',
            fontWeight: activeTab === 'summary' ? 'bold' : 'normal'
          }}
        >
          📊 Dagoppsummering
        </button>
      </div>

      {/* Datovelger */}
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <label style={{ fontWeight: 'bold' }}>Dato:</label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <button
          onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
          style={{
            padding: '8px 16px',
            background: '#f0f0f0',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          I dag
        </button>
        <button
          onClick={() => activeTab === 'briefing' ? loadBriefing() : loadSummary()}
          disabled={loading}
          style={{
            padding: '8px 16px',
            background: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Laster...' : 'Oppdater'}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Laster...</p>
        </div>
      ) : (
        <>
          {/* Briefing Tab */}
          {activeTab === 'briefing' && briefing && (
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {/* Quick Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '30px'
              }}>
                <StatCard
                  icon={<AlertCircle size={24} color="#dc3545" />}
                  title="Frister i dag"
                  value={briefing.data.frister.length}
                  color="#dc3545"
                />
                <StatCard
                  icon={<CheckCircle size={24} color="#28a745" />}
                  title="Oppgaver"
                  value={briefing.data.oppgaver.length}
                  color="#28a745"
                />
                <StatCard
                  icon={<Calendar size={24} color="#007bff" />}
                  title="Møter"
                  value={briefing.data.moter.length}
                  color="#007bff"
                />
                <StatCard
                  icon={<TrendingUp size={24} color="#ffc107" />}
                  title="Saker i dag"
                  value={briefing.data.sakerMedAktivitetIdag || 0}
                  color="#ffc107"
                />
              </div>

              {/* Briefing tekst */}
              <div style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                lineHeight: '1.6',
                color: '#333'
              }}>
                {renderMarkdown(briefing.briefing)}
              </div>
            </div>
          )}

          {/* Summary Tab */}
          {activeTab === 'summary' && summary && (
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {/* Quick Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '30px'
              }}>
                <StatCard
                  icon={<Clock size={24} color="#6f42c1" />}
                  title="Tid registrert"
                  value={summary.stats.tid_registrert}
                  color="#6f42c1"
                />
                <StatCard
                  icon={<CheckCircle size={24} color="#28a745" />}
                  title="Oppgaver fullført"
                  value={summary.stats.oppgaver_fullfort}
                  color="#28a745"
                />
                <StatCard
                  icon={<TrendingUp size={24} color="#007bff" />}
                  title="Nye saker"
                  value={summary.stats.nye_saker}
                  color="#007bff"
                />
                <StatCard
                  icon={<Mail size={24} color="#17a2b8" />}
                  title="E-poster sendt"
                  value={summary.stats.eposter_sendt}
                  color="#17a2b8"
                />
              </div>

              {/* Summary tekst */}
              <div style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                lineHeight: '1.6',
                color: '#333'
              }}>
                {renderMarkdown(summary.summary)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Stat Card komponent
 */
function StatCard({ icon, title, value, color }) {
  return (
    <div style={{
      background: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      border: `2px solid ${color}20`,
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    }}>
      <div>{icon}</div>
      <div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{title}</div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color }}>{value}</div>
      </div>
    </div>
  );
}
